import { useEffect, useState } from 'react'

/**
 * Render a shared Excel workbook in the meeting.
 *
 * Same problem as `.docx`, same answer: a browser has no spreadsheet viewer, so
 * either it is converted somewhere or the room is told the file cannot be shown.
 * It used to be the latter — presenting a `.xlsx` put "This file type cannot be
 * previewed in the browser" on every participant's stage, which is a download
 * link dressed up as a document share.
 *
 * Parsed in the page, so the workbook never leaves the participant's machine.
 * Office Online and Google's viewer both require the file to be fetchable by a
 * third party, which would hand every spreadsheet shared in any meeting to
 * them — the same trade `useSharedDocx` declined, for the same reason.
 *
 * ## Why not SheetJS
 *
 * `xlsx` is the obvious library and is not used here. Its last npm publish is
 * 0.18.5, and npm carries two HIGH advisories against it — prototype pollution
 * (fixed in 0.19.3) and a ReDoS (fixed in 0.20.2) — both reporting
 * `patched: None`, because SheetJS moved distribution to its own CDN and never
 * published the fixes to npm. The input here is a file chosen by whoever is in
 * the meeting, parsed in the origin that holds the room token, so a known
 * prototype-pollution sink is exactly the wrong thing to point at it.
 *
 * `read-excel-file` is maintained, adds no advisories of its own, and reads the
 * one thing needed: cell values, by sheet.
 *
 * Legacy `.xls` is NOT covered — it is a binary OLE container rather than a zip
 * of XML, the same reason `.doc` is not. It is detected so it can be reported
 * honestly instead of failing as an unexplained blank frame.
 */

/** Rows the grid will draw for one sheet, already stringified. */
export type SheetGrid = {
  name: string
  rows: string[][]
  /** Rows were dropped to keep the table renderable. */
  truncated: boolean
  /** How many rows the sheet actually had, truncated or not. */
  totalRows: number
}

/**
 * Matches the CSV viewer's cap. A spreadsheet is the format most likely to
 * arrive with tens of thousands of rows, and every participant renders this —
 * one person sharing an export should not drop the frame rate of the whole
 * room.
 */
export const MAX_ROWS = 500

/** Does this share look like an Excel workbook? */
export function isXlsxShare(mime: string, filename?: string | null): boolean {
  const m = (mime || '').toLowerCase().split(';')[0].trim()
  if (m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true
  return /\.xlsx(?:[?#]|$)/i.test(filename ?? '')
}

/**
 * Legacy binary Excel. Detected only so it can be reported honestly.
 *
 * The `(?:[?#]|$)` is what keeps this off `.xlsx` — without it every modern
 * workbook would match here first and be declared unreadable.
 *
 * The filename is asked FIRST, and the mime only answers when there is no
 * extension to contradict it. `application/vnd.ms-excel` sits on this
 * codebase's own `UNRELIABLE_MIMES` list (`share-upload.ts`) for a good reason:
 * a browser reports whatever the OS registered for the extension, and wherever
 * Excel owns `.csv` — the Windows default — a plain CSV is uploaded under it.
 * Trusting it outright would answer "this is a legacy Excel file, save it as
 * .xlsx" to someone sharing a perfectly readable CSV.
 */
export function isLegacyXlsShare(mime: string, filename?: string | null): boolean {
  if (/\.xls(?:[?#]|$)/i.test(filename ?? '')) return true
  // Any other extension is the file telling us what it is, and it outranks a
  // type the uploading OS guessed.
  if (/\.[a-z0-9]{1,5}(?:[?#]|$)/i.test(filename ?? '')) return false
  return (mime || '').toLowerCase().split(';')[0].trim() === 'application/vnd.ms-excel'
}

/**
 * One cell, as text.
 *
 * Dates are the reason this is not `String(value)`. `read-excel-file` returns a
 * real `Date` for a date-formatted cell, and `String(new Date())` is
 * "Wed Sep 16 2026 00:00:00 GMT+0530 (India Standard Time)" — the whole
 * timezone tail, in every cell of a date column, in a table the room is trying
 * to read. ISO is short, sorts correctly and means the same thing in every
 * locale in the meeting.
 *
 * A time-of-day component is kept only when there is one, so a plain date does
 * not grow a meaningless "00:00".
 */
export function formatCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return ''
    const iso = value.toISOString()
    return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso.slice(0, 16).replace('T', ' ')
  }
  // Excel's own rendering of a boolean cell, so a sheet looks like itself.
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  return String(value)
}

/** Is every cell in this row empty? */
const blankRow = (row: readonly string[]) => row.every((c) => c === '')

/**
 * Turn one parsed sheet into a drawable grid.
 *
 * Trailing blank rows and columns are dropped. A workbook whose used range was
 * ever larger than its content — which is most of them, because deleting rows
 * in Excel does not shrink it — otherwise renders as the data followed by a
 * screen of empty cells, and the viewer has no way to tell that from the sheet
 * simply being empty.
 *
 * Only TRAILING blanks go. A blank row between two tables is part of how the
 * sheet is laid out and removing it would silently rewrite the document.
 */
export function toGrid(name: string, data: readonly (readonly unknown[])[]): SheetGrid {
  const all = data.map((row) => row.map(formatCell))

  let lastRow = all.length
  while (lastRow > 0 && blankRow(all[lastRow - 1])) lastRow -= 1

  let width = 0
  for (let r = 0; r < lastRow; r++) {
    for (let c = all[r].length - 1; c >= 0; c--) {
      if (all[r][c] !== '') {
        width = Math.max(width, c + 1)
        break
      }
    }
  }

  const totalRows = lastRow
  const kept = all.slice(0, Math.min(lastRow, MAX_ROWS))
  // Padded, not just trimmed: a short row would otherwise end the HTML row
  // early and pull every cell to its right out of its column.
  const rows = kept.map((row) =>
    Array.from({ length: width }, (_, c) => row[c] ?? ''),
  )

  return { name, rows, truncated: totalRows > MAX_ROWS, totalRows }
}

/** Does this workbook have anything to show at all? */
export function hasContent(sheets: readonly SheetGrid[]): boolean {
  return sheets.some((s) => s.rows.length > 0)
}

export function useSharedSheet(
  objectUrl: string,
  enabled: boolean,
): { sheets: SheetGrid[] | null; loading: boolean; error: string | null } {
  const [sheets, setSheets] = useState<SheetGrid[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!objectUrl || !enabled) {
      setSheets(null)
      setError(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    ;(async () => {
      try {
        // Imported on demand, like the Word converter: only whoever opens a
        // spreadsheet needs the parser, and it has no business in the entry
        // chunk of a video call.
        // `/browser` rather than the bare name: the package ships no root
        // entry, only per-environment ones, and the browser build is the one
        // that does not reach for node:fs.
        const { default: readXlsxFile } = await import('read-excel-file/browser')
        const blob = await (await fetch(objectUrl)).blob()
        // v9 returns every sheet from one call — `{ sheet, data }[]`. Earlier
        // versions needed `getSheets: true` and then a pass per sheet; that API
        // is gone, and calling it that way silently yields one sheet.
        const parsed = await readXlsxFile(blob)
        if (cancelled) return
        setSheets(parsed.map((s) => toGrid(s.sheet, s.data as unknown[][])))
      } catch (err: unknown) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Could not read this spreadsheet.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [objectUrl, enabled])

  return { sheets, loading, error }
}
